<template>
  <ZtPopup v-if="isAvailable" placement="bottom-start" :interactive="true" :teleport="true" data-testid="viewer-translation">
    <MetaButton tooltip="View in another language" tooltip-placement="top-start" data-testid="viewer-translation-trigger">
      <Icon :name="isLoading ? 'general.spinner' : 'general.globe'" size="sm" :class="{ 'animate-spin': isLoading }" />
      <span data-testid="viewer-translation-current">{{ currentLabel }}</span>
      <Icon name="general.chevronDown" size="sm" />
    </MetaButton>
    <template #content="{ close }">
      <DropdownSelectorWrapper test-id-prefix="viewer-translation">
        <div class="flex flex-col">
          <div class="flex gap-2">
            <div v-for="(column, index) in columns" :key="index" class="dropdown-column" :data-testid="`viewer-translation-column-${index}`">
              <DropdownSelectorItem
                v-for="option in column"
                :key="option.value"
                :icon="option.icon"
                :label="option.label"
                :value="option.value"
                :selected="option.value === selectedValue"
                :data-testid="`viewer-translation-item-${option.value}`"
                @select="onSelect($event, close)"
              />
            </div>
          </div>
          <p v-if="isCurrentItemOwner" class="owner-hint" data-testid="viewer-translation-owner-hint">
            Only changes your view. Viewers pick their own language.
          </p>
        </div>
      </DropdownSelectorWrapper>
    </template>
  </ZtPopup>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon } from '@/storybook';
import { ZtPopup } from '@/storybook/popup';
import { DropdownSelectorWrapper, DropdownSelectorItem } from '@/storybook/form/dropdownSelector';
import MetaButton from '@/modules/viewer/components/MetaButton.vue';
import { useFeatures } from '@/composables/useFeatures';
import { useToast } from '@/composables/useToast';
import { useUser } from '@/modules/viewer/composables/useUser';
import { TrackEvents, useViewerTrack } from '@/modules/viewer/composables/useViewerTrack';
import { ORIGINAL_ICON, ORIGINAL_LANGUAGE, languageIcon, languageLabel } from '@/modules/viewer/composables/useLanguages';
import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { ItemType, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { TRANSLATION_FEATURE_FLAG, useTranslationStore } from '@/modules/viewer/store/useTranslationStore';

interface LanguageOption {
  icon: string;
  label: string;
  value: string;
}

const itemStore = useViewerItemStore();
const translationStore = useTranslationStore();
const { getItemFeature } = useFeatures();
const { error: toastError } = useToast();
const { trackItemOwner } = useViewerTrack();
const { isCurrentItemOwner } = useUser();

const { type: itemType, originalLanguage, description } = storeToRefs(itemStore);
const { availableLanguages, selectedLanguage, isLoading, error } = storeToRefs(translationStore);
const { hasChapters } = storeToRefs(useAiMetadataStore());
const { isTranscriptionMissingContent } = storeToRefs(useTranscriptionStore());

const translationEnabled = getItemFeature(TRANSLATION_FEATURE_FLAG, false);

// Mirrors SegmentTranslator.translatable?: the title alone is not content to
// translate, a typed description on a silent video is.
const hasTranslatableContent = computed(() => !!description.value || hasChapters.value || !isTranscriptionMissingContent.value);

const isAvailable = computed(
  () => translationEnabled.value && itemType.value === ItemType.VIDEO && availableLanguages.value.length > 0 && hasTranslatableContent.value
);

// The item's own language leads the list. Until detection ran, or when it is
// outside the configured list, the entry reads "Original".
const originalOption = computed<LanguageOption>(() => {
  const language = originalLanguage.value;
  if (language && availableLanguages.value.includes(language)) {
    return { icon: languageIcon(language), label: languageLabel(language), value: ORIGINAL_LANGUAGE };
  }
  return { icon: ORIGINAL_ICON, label: 'Original', value: ORIGINAL_LANGUAGE };
});

const options = computed<LanguageOption[]>(() => {
  const translated = availableLanguages.value
    .filter(language => language !== originalLanguage.value)
    .map(language => ({ icon: languageIcon(language), label: languageLabel(language), value: language }));

  return [originalOption.value, ...translated];
});

const columns = computed(() => {
  const half = Math.ceil(options.value.length / 2);
  return [options.value.slice(0, half), options.value.slice(half)];
});

const selectedValue = computed(() => selectedLanguage.value ?? ORIGINAL_LANGUAGE);

const currentLabel = computed(() => {
  if (!selectedLanguage.value) {
    return originalOption.value.label;
  }
  return languageLabel(selectedLanguage.value);
});

const onSelect = (value: string, close: () => void) => {
  close();
  translationStore.select(value, { explicit: true });
  trackItemOwner(TrackEvents.VIEWER_TRANSLATION_SELECTED, { toLanguage: value });
};

watch(error, message => {
  if (message) {
    toastError(message);
  }
});
</script>

<style scoped>
.dropdown-column {
  @apply rounded-10 flex flex-col items-start p-2.5 gap-2.5 flex-1;
}

.owner-hint {
  @apply px-2.5 pb-1.5 text-xs text-zinc-500 dark:text-zinc-400;
}
</style>
